import React from 'react'
import { Link } from 'react-router-dom'
import { DateTime } from 'luxon'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../ui/card'
import { Button } from '../../../ui/button'
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert'
import { CalendarDays, Pencil, AlertTriangle } from 'lucide-react'
import { Event as EventModel } from '../../../../model/Event'
import { SetupStepState } from '../eventSetupStatus'

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

const formatDate = (date: Date | string | number | undefined): string => {
  if (!date) return 'Not set'
  try {
    let dt: DateTime
    if (typeof date === 'number') dt = DateTime.fromMillis(date)
    else if (typeof date === 'string') dt = DateTime.fromISO(date)
    else dt = DateTime.fromJSDate(date)
    return dt.isValid ? dt.toFormat('MMM d, yyyy') : 'Not set'
  } catch {
    return 'Not set'
  }
}

interface BasicsStepProps {
  event: EventModel
  step: SetupStepState
  canEdit: boolean
}

/**
 * Read-only confirmation of the settings that silently break scheduling when
 * they're wrong. Editing happens on the Basic Info tab rather than being
 * duplicated here.
 */
export const BasicsStep: React.FC<BasicsStepProps> = ({ event, step, canEdit }) => {
  const dayCount =
    event.startDate && event.endDate
      ? Math.max(
          1,
          Math.round(
            DateTime.fromJSDate(new Date(event.endDate as any))
              .startOf('day')
              .diff(
                DateTime.fromJSDate(new Date(event.startDate as any)).startOf('day'),
                'days'
              ).days
          ) + 1
        )
      : null

  const rows: Array<{ label: string; value: string; muted?: boolean }> = [
    { label: 'Start date', value: formatDate(event.startDate) },
    { label: 'End date', value: formatDate(event.endDate) },
    {
      label: 'Length',
      value: dayCount ? `${dayCount} day${dayCount === 1 ? '' : 's'}` : '—',
      muted: true,
    },
    {
      label: 'Time zone',
      value:
        event.timezone ||
        (event.client?.timezone
          ? `${event.client.timezone} (from client)`
          : 'Not set'),
    },
    {
      label: 'First day of week',
      value: DAY_NAMES[event.firstDayOfWeek ?? 0] ?? 'Sunday',
    },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <CalendarDays className="h-4 w-4" />
          Confirm the event basics
        </CardTitle>
        <CardDescription>{step.help}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="divide-y rounded-md border">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex items-center justify-between px-3 py-2.5 text-sm"
            >
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className={row.muted ? 'text-muted-foreground' : 'font-medium'}>
                {row.value}
              </dd>
            </div>
          ))}
        </dl>

        {!step.done && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>This event has no date range</AlertTitle>
            <AlertDescription>
              Without a start and end date there are no days to pick when creating a
              shift. Set the dates on the Basic Info tab before continuing.
            </AlertDescription>
          </Alert>
        )}

        {step.warnings.map((warning) => (
          <Alert key={warning}>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{warning}</AlertDescription>
          </Alert>
        ))}

        {canEdit && (
          <Button asChild variant="outline" size="sm">
            <Link to={`/event/${event.id}/config/info`}>
              <Pencil className="mr-2 h-3.5 w-3.5" />
              Edit event details
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
